import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Flame, X, ChevronRight } from 'lucide-react';
import { API, authFetch } from '@/App';

const MESSAGES = [
  { text: "Don't break your streak!", sub: "Check in now" },
  { text: "Your routine is waiting!", sub: "Keep the chain going" },
  { text: "Stay consistent, stay glowing", sub: "Tap to check in" },
  { text: "Almost there today!", sub: "Complete your routine" },
];

export default function RoutineReminderBanner() {
  const navigate = useNavigate();
  const [reminder, setReminder] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    checkRoutines();
  }, []);

  const checkRoutines = async () => {
    try {
      const res = await authFetch(`${API}/routines`);
      if (!res.ok) return;

      const routines = await res.json();
      if (!routines.length) return;

      // Find routines not checked in today
      const unchecked = routines.filter(r => !r.today_checkin?.completed);
      if (!unchecked.length) return;

      // Pick the one with the highest streak (most at risk)
      const atRisk = unchecked.sort((a, b) => (b.current_streak || 0) - (a.current_streak || 0))[0];
      const streak = atRisk.current_streak || 0;

      // Time-aware messaging
      const hour = new Date().getHours();
      let contextMsg;
      if (atRisk.schedule === 'morning' && hour < 12) {
        contextMsg = "Good morning! Time for your AM routine";
      } else if (atRisk.schedule === 'night' && hour >= 18) {
        contextMsg = "Wind down with your PM routine";
      } else if (streak > 0) {
        contextMsg = MESSAGES[0].text;
      } else {
        contextMsg = MESSAGES[Math.floor(Math.random() * MESSAGES.length)].text;
      }

      setReminder({
        routineId: atRisk.routine_id,
        name: atRisk.name,
        streak,
        uncheckedCount: unchecked.length,
        message: contextMsg,
        checkedProducts: atRisk.today_checkin?.products_checked?.length || 0,
        totalProducts: atRisk.products?.length || 0,
      });
    } catch (e) {
      // Silently fail — no reminder is fine
    }
  };

  if (!reminder || dismissed) return null;

  return (
    <button
      data-testid="routine-reminder-banner"
      onClick={() => navigate('/routines')}
      className="w-full bg-gradient-to-r from-orange-500 via-rose-500 to-pink-500 text-white px-4 py-3 flex items-center gap-3 group transition-all hover:brightness-110 relative"
    >
      {/* Dismiss button */}
      <div
        className="absolute top-1 right-1 p-1 rounded-full hover:bg-white/20 transition-colors z-10"
        onClick={(e) => { e.stopPropagation(); setDismissed(true); }}
        data-testid="dismiss-reminder"
      >
        <X className="w-3.5 h-3.5 text-white/70" />
      </div>

      {/* Flame with streak */}
      <div className="relative shrink-0">
        <Flame className="w-7 h-7 text-white" />
        {reminder.streak > 0 && (
          <span className="absolute -bottom-1 -right-1 bg-white text-orange-500 text-[10px] font-black w-4 h-4 rounded-full flex items-center justify-center">
            {reminder.streak}
          </span>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 text-left min-w-0">
        <p className="text-sm font-bold leading-tight">{reminder.message}</p>
        <p className="text-xs opacity-80 mt-0.5">
          {reminder.name}
          {reminder.checkedProducts > 0 && ` • ${reminder.checkedProducts}/${reminder.totalProducts} done`}
          {reminder.uncheckedCount > 1 && ` • +${reminder.uncheckedCount - 1} more`}
        </p>
      </div>

      {/* Arrow */}
      <ChevronRight className="w-5 h-5 text-white/70 group-hover:translate-x-0.5 transition-transform shrink-0" />
    </button>
  );
}
